import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { useCompactViewport } from '@/components/motion'
import { GeoMark, type GeoVariant } from '@/components/landing/Editorial'
import { OUT_EASE } from './halftone'

export interface ProcessCard {
  title: string
  description: string
  /** Short outcomes listed under the copy. */
  outcomes?: string[]
}

const MARKS: Array<{ geo: GeoVariant; color: 'blue' | 'purple' | 'pink' }> = [
  { geo: 'cube', color: 'blue' },
  { geo: 'ring', color: 'purple' },
  { geo: 'bars', color: 'pink' },
  { geo: 'chevrons', color: 'blue' },
]

/**
 * Four white bordered cards in a zig-zag row (reference "process cards"):
 * overlapping through negative margins, rotated −5° / +5° in turn, cards 2
 * and 4 raised 10rem. On hover a card straightens and comes forward. Below
 * lg they fall into a grid with a gentle ±2° tilt; phones render static.
 */
export function RotatedProcessCards({ cards, className }: { cards: ProcessCard[]; className?: string }) {
  const compact = useCompactViewport()
  return (
    <ol className={cn('grid gap-6 sm:grid-cols-2 lg:flex lg:gap-0 lg:pt-40', className)}>
      {cards.map((card, i) => {
        const mark = MARKS[i % MARKS.length]!
        const odd = i % 2 === 1
        return (
          <motion.li
            key={card.title}
            className="relative lg:-ml-6 lg:w-[calc(25%+1.125rem)] lg:shrink-0 lg:first:ml-0 hover:z-10"
            initial={compact ? false : { opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '0px 0px -10% 0px' }}
            transition={{ duration: 0.8, ease: OUT_EASE, delay: i * 0.1 }}
          >
            <div
              className={cn(
                'flex h-full flex-col justify-between gap-10 border border-[var(--color-landing-gray-200)] bg-white p-7 transition-[rotate,translate,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-[var(--color-landing-gray-800)] dark:bg-[#12121a] md:p-9 lg:min-h-[25rem] lg:pr-12',
                odd ? 'rotate-[2deg] lg:rotate-[5deg] lg:-translate-y-40' : 'rotate-[-2deg] lg:rotate-[-5deg]',
                'hover:rotate-0 hover:shadow-[0_30px_60px_-30px_rgba(10,10,10,0.35)]'
              )}
            >
              <GeoMark variant={mark.geo} color={mark.color} className="h-12 w-12 shrink-0 md:h-14 md:w-14" />
              <div>
                <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
                  {card.title}
                </h3>
                <p className="landing-text-body-sm mt-4 text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
                  {card.description}
                </p>
                {card.outcomes && card.outcomes.length > 0 && (
                  <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 text-sm font-medium text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                    {card.outcomes.map((outcome) => (
                      <li key={outcome} className="flex items-center gap-2">
                        <span aria-hidden className="block h-1.5 w-1.5 bg-[var(--color-landing-accent)]" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.li>
        )
      })}
    </ol>
  )
}

export default RotatedProcessCards
